import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import { H1 } from '@ep/templates/ProductPage';
import theme from '@ep/theme';
import { Gcms_RichText, Gcms_TabPost } from '@ep/types/gatsbyTypes';
import useScroll from '@ep/utils/useScroll';
import VolumeOff from '@mui/icons-material/VolumeOff';
import VolumeUp from '@mui/icons-material/VolumeUp';
import { IconButton } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Fade from 'react-reveal';
import TabPost from './TabPost';

export interface TabCarouselModuleProps {
  label?: string;
  tabHeading: string;
  tabSubHeading: string;
  tabDescription: Gcms_RichText;
  tabs: Gcms_TabPost[];
  backgroundColor?: string;
}

const StyledContainer = styled(Container)<TabCarouselModuleProps>`
  max-width: 100%;
  margin-left: 0rem !important;
  margin-right: 0rem !important;
  padding-top: 5rem;
  padding-bottom: 1rem;
  background-color: ${theme.colors.lightGray};
  ${({ tabSubHeading }) => (tabSubHeading ? `height: 75rem;` : 'height: 60rem;')}
  @media (max-width: ${theme.breakpoints.optimalMobile + 700}px) {
    height: 90rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 480}px) {
    height: 95rem;
  }
  ${theme.mediaQueries.mobile} {
    height: 85rem;
  }
  h1 {
    max-width: 70rem;
    margin: auto auto;
  }
`;

const Grid = styled.div`
  max-width: 80rem;
  margin: auto auto;
  display: flex;
  justify-content: center;
  @media (max-width: ${theme.breakpoints.optimalMobile + 1050}px) {
    grid-template-columns: repeat(1, 1fr);
    display: block;
    height: 50rem;
  }
`;

const PostDiv = styled.div`
  height: 40rem;
  max-width: 100%;
  display: flex;
  position: relative;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  ::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 700}px) {
    height: 80rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 600}px) {
    height: 70rem;
  }
  ${theme.mediaQueries.mobile} {
    height: 85rem;
    margin: auto auto;
  }
`;

interface PageIndicatorsProps {
  tabsLength: number;
}

const PageIndicators = styled.div<PageIndicatorsProps>`
  margin: auto auto;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.epGray};
  max-width: 75rem;
  display: flex;
  align-items: center;
  justify-content: ${({ tabsLength }) =>
    tabsLength < 4 ? `space-around;` : `center;`};
  line-height: 1.7rem;
  @media (max-width: ${theme.breakpoints.optimalMobile + 700}px) {
    max-width: 35rem;
    margin: auto auto;
    margin-bottom: 2rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 450}px) {
    width: 30rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 400}px) {
    font-size: 1.5rem;
    width: 20rem;
    text-align: center;
    margin: auto auto;
    display: flex;
    flex-direction: column;
    margin-bottom: 2rem;
  }
`;

interface PageIndicatorProps {
  active: boolean;
  themeColor?: string;
}

const PageIndicator = styled.div<PageIndicatorProps>`
  width: 17rem;
  text-align: center;
  font-family: ${theme.typography.primaryFont.regular};
  padding-bottom: 1rem;
  margin-top: 1.2rem;
  margin-left: 1rem;
  margin-right: 1rem;
  background-color: transparent;
  cursor: pointer;
  ${theme.mediaQueries.mobile} {
    padding-bottom: 0.5rem;
  }
  ${({ active }) =>
    active
      ? `color: ${theme.colors.oceanBlue}; font-weight: bold; border-bottom: 5px solid ${theme.colors.oceanBlue};`
      : `color: ${theme.colors.epGray}; border-bottom: 5px solid ${theme.colors.lightGray};`};
`;

const PostsContainer = styled.div`
  height: 35rem;
  max-width: 80rem;
  @media (max-width: ${theme.breakpoints.optimalTablet + 400}px) {
    margin-left: 0rem;
    height: 37rem;
  }
  ${theme.mediaQueries.mobile} {
    margin: auto auto;
    width: 100%;
    height: 30rem;
  }
`;

const StyledIcon = styled(IconButton)`
  z-index: 1;
  position: absolute;
  top: 46rem;
  @media (max-width: ${theme.breakpoints.optimalMobile + 700}px) {
    left: 13rem;
    top: 52rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 645}px) {
    left: 12rem;
    top: 48rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 450}px) {
    left: 10rem;
    top: 57rem;
  }
  @media (max-width: ${theme.breakpoints.optimalMobile + 400}px) {
    left: 3rem;
    top: 51rem;
  }
  ${theme.mediaQueries.tablet} {
    left: 9rem;
    top: 57rem;
  }
  ${theme.mediaQueries.mobile} {
    left: 2rem;
    top: 45rem;
  }
`;

const Title = styled.h2`
  white-space: wrap;
  max-width: 70rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem auto 1rem;
  color: ${theme.colors.duskBlue};
  font-family: ${theme.typography.semiBoldFont};
  font-size: 2.625rem;
  line-height: 1;
  ${theme.mediaQueries.tablet} {
    font-size: 2rem;
  }
  ${theme.mediaQueries.mobile} {
    font-size: 1.8rem;
  }
  `;

const SubTitle = styled.h3`
  white-space: wrap;
  max-width: 55rem;
  text-align: center;
  padding: 1rem;
  margin: 0rem auto 4rem;
  font-family: ${theme.typography.secondaryFont.light};
  font-size: 1.5rem;
  ${theme.mediaQueries.tablet} {
    font-size: 1.4rem;
  }
  ${theme.mediaQueries.mobile} {
    font-size: 1.2rem;
  }
  line-height: 1.5;

`;

const TabCarouselModule = ({
  tabHeading,
  tabSubHeading,
  tabs,
  backgroundColor,
}: TabCarouselModuleProps) => {
  const postsRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLDivElement>(null);
  const { x } = useScroll(postsRef);
  const [onMute, setMute] = useState(true);
  const [playAudio, setPlayAudio] = useState(false);
  const currentIndex = postsRef.current
    ? Math.floor(x / postsRef.current.clientWidth)
    : 0;

  const scrollTo = (index: number) => {
    postsRef.current?.scroll({
      left: (postsRef.current.children[index] as HTMLElement).offsetLeft,
      behavior: 'smooth',
    });
  };

  // toggle the audio mute to unmute
  const toggleMute = () => {
    setMute(!onMute);
  };

  // play audio when scrolled to
  const handleReveal = () => {
    setPlayAudio(true);
  };

  useEffect(() => {
    let elements =
      tabRef.current && tabRef.current.children ? tabRef.current.children : [];
    for (let i = 0; i < elements.length; i++) {
      let player: HTMLAudioElement = elements[i]
        .children[0] as HTMLAudioElement;
      if (player != null && i === currentIndex) {
        if (onMute) {
          player.pause();
        } else {
          if (playAudio) {
            player
              .play()
              .catch(err => console.error('Something went wrong', err));
          } else {
            player.pause();
          }
        }
      } else {
        if (player != null) {
          player.pause();
          player.currentTime = 0;
        }
      }
    }
  }, [playAudio, onMute, currentIndex]);

  // const handleTabClick = (tabLabel: string) => {
  //   dataLayer.push({
  //     event:
  //       'Page Interaction – user clicked on tab (in carousel) - ' + tabLabel,
  //   });
  // };

  return (
    <StyledContainer backgroundColor={backgroundColor} tabSubHeading={tabSubHeading}>
      {tabHeading && <Title>{tabHeading}</Title>}
      {tabSubHeading && <SubTitle>{tabSubHeading}</SubTitle>}
      <Grid>
        <PostsContainer>
          <Fade onReveal={() => handleReveal()} />
          <PageIndicators tabsLength={tabs.length} ref={tabRef}>
            {tabs &&
              tabs.length > 1 &&
              tabs.map((tabPost: Gcms_TabPost, index) => (
                <PageIndicator
                  key={tabPost.id}
                  active={currentIndex === index}
                  onClick={() => {
                    // handleTabClick(tabPost.label); 
                    scrollTo(index);
                  }}
                  themeColor={backgroundColor}
                >
                  {/* The Audio Player will be hidden */}
                  {tabPost.soundFile && (
                    <audio
                      key={index}
                      controls
                      src={tabPost.soundFile.url}
                      playsInline
                      hidden
                    />
                  )}
                  {tabPost.label.substring(0, 50)}
                </PageIndicator>
              ))}
          </PageIndicators>
          {tabs[currentIndex].soundFile && (
            <StyledIcon onClick={() => toggleMute()}>
              {onMute ? <VolumeOff /> : <VolumeUp />}
            </StyledIcon>
          )}
          <PostDiv ref={postsRef} tabIndex={0}>
            {tabs && tabs.map(tab => <TabPost key={tab.id} tabPost={tab} />)}
          </PostDiv>
        </PostsContainer>
      </Grid>
    </StyledContainer>
  );
};

export default TabCarouselModule;
